import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  Button,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  Rating,
  Pagination,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WorkIcon from '@mui/icons-material/Work';
import InfoIcon from '@mui/icons-material/Info';
import SchoolIcon from '@mui/icons-material/School';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import CategoryIcon from '@mui/icons-material/Category';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ChatIcon from '@mui/icons-material/Chat';
import EditIcon from '@mui/icons-material/Edit';
import { Star as StarIcon } from '@mui/icons-material';
import { getExpertById, getExpertReviews, ExpertReview } from '@services/expert.service';
import { ExpertDetailsDto } from '@/types/expert';
import { ConsultationRequestModal } from '@components/experts/ConsultationRequestModal';
import { ConnectionPermissionsDialog } from '@components/experts/ConnectionPermissionsDialog';
import { ClientAppointmentsList } from '@components/appointments/client/ClientAppointmentsList';
import { UserAvatar } from '@components/common/UserAvatar';
import { useAppDispatch } from '../../store';
import { createConversation } from '../../store/chat/chatSlice';
import { toastService } from '@services/toast.service';
import { PageIndices, TabIndices } from '@/common/i-doc/i-doc.registry';
import { PageHint } from '@components/common/PageHint';

const FIELD_LABELS: Record<string, string> = {
  about: 'Про мене',
  experience_years: 'Досвід',
  consultation_rate: 'Вартість консультації',
  location: 'Місцезнаходження',
  education: 'Освіта',
  certificates: 'Сертифікати',
  working_hours: 'Робочі години',
};

const FIELD_ICONS: Record<string, React.ReactNode> = {
  about: <InfoIcon />,
  experience_years: <WorkIcon />,
  consultation_rate: <PriceChangeIcon />,
  location: <LocationOnIcon />,
  education: <SchoolIcon />,
  certificates: <CardMembershipIcon />,
  working_hours: <AccessTimeIcon />,
};

const renderField = (key: string, value: string | number | null, unit = '') => {
  if (value === null || value === '') return null;
  return (
    <ListItem disablePadding sx={{ mb: 1 }}>
      <ListItemIcon sx={{ minWidth: 40 }}>{FIELD_ICONS[key]}</ListItemIcon>
      <ListItemText primary={FIELD_LABELS[key]} secondary={`${value}${unit}`} />
    </ListItem>
  );
};

const ExpertPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [expert, setExpert] = useState<ExpertDetailsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);

  const [consultationOpen, setConsultationOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(false);

  const [reviews, setReviews] = useState<ExpertReview[]>([]);
  const [reviewsTotal, setReviewsTotal] = useState(0);
  const [reviewsPage, setReviewsPage] = useState(1);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const REVIEWS_LIMIT = 10;

  const expertId = Number(id);

  const reload = () => getExpertById(expertId).then(setExpert);

  const loadReviews = (page: number) => {
    setReviewsLoading(true);
    getExpertReviews(expertId, { page, limit: REVIEWS_LIMIT })
      .then(res => { setReviews(res.data); setReviewsTotal(res.meta.total); })
      .catch(() => {})
      .finally(() => setReviewsLoading(false));
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getExpertById(expertId)
      .then(data => setExpert(data))
      .catch(() => setError('Не вдалося завантажити профіль експерта.'))
      .finally(() => setLoading(false));
  }, [id]);

  const handleStartChat = async () => {
    if (!expert) return;
    try {
      await dispatch(createConversation({ targetUserId: expertId })).unwrap();
      navigate('/chat');
    } catch {
      toastService.error('Не вдалося відкрити чат.');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !expert) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error ?? 'Експерта не знайдено.'}</Alert>
      </Container>
    );
  }

  const { general, professional, social, specializations, access } = expert;
  const { interactionStatus } = general;
  const isActive = interactionStatus === 'active';
  const isPending = interactionStatus === 'pending';

  const expertAsListItem = { id: expertId, name: general.name, avatar: general.avatar, createdAt: expert.createdAt, gender: general.gender, specializationsString: general.specializationsString, interactionStatus };

  return (
    <Container maxWidth="md" data-i-doc={PageIndices.CLIENT_EXPERT}>
      <PageHint iDoc={PageIndices.CLIENT_EXPERT} />
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} size="small">
          <ArrowBackIcon />
        </IconButton>
        <UserAvatar path={general.avatar} name={general.name} sx={{ width: 56, height: 56 }} />
        <Box>
          <Typography variant="h5" fontWeight={600}>{general.name}</Typography>
          <Typography variant="body2" color="text.secondary">{general.specializationsString}</Typography>
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
          {(isActive || isPending) && (
            <Button
              variant="contained"
              disabled={isPending}
              onClick={() => setConsultationOpen(true)}
            >
              {isPending ? 'Очікує' : 'Нова зустріч'}
            </Button>
          )}
          {isActive && (
            <Tooltip title="Написати в чат">
              <IconButton color="primary" onClick={handleStartChat}>
                <ChatIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Divider />

      <Tabs value={tab} onChange={(_, v) => { setTab(v); if (v === (isActive ? 3 : 2)) loadReviews(1); }} sx={{ mb: 3 }}>
        <Tab label="Основна інформація" />
        <Tab label="Зустрічі" />
        {isActive && <Tab label="Доступи" />}
        <Tab label="Відгуки" />
      </Tabs>

      {/* Tab 0: Основна інформація */}
      {tab === 0 && (
        <Grid container spacing={4} data-i-doc={TabIndices.EXPERT_PROFILE_MAIN}>
          <Grid item xs={12}>
            <PageHint iDoc={TabIndices.EXPERT_PROFILE_MAIN} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Професійна інформація</Typography>
            <List dense>
              {renderField('about', professional.about)}
              {renderField('experience_years', professional.experience_years, ' років')}
              {renderField('consultation_rate', professional.consultation_rate, ' грн')}
              {renderField('location', professional.location)}
              {renderField('education', professional.education)}
              {renderField('certificates', professional.certificates)}
              {renderField('working_hours', professional.working_hours)}
            </List>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>Спеціалізації</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
              {specializations.map(spec => (
                <Chip key={spec.code} label={spec.name} size="small" icon={<CategoryIcon />} />
              ))}
            </Box>

            <Typography variant="h6" gutterBottom>Соціальні мережі</Typography>
            <List dense>
              {social.facebook && (
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}><FacebookIcon /></ListItemIcon>
                  <ListItemText primary="Facebook" secondary={<a href={social.facebook} target="_blank" rel="noopener noreferrer">{social.facebook}</a>} />
                </ListItem>
              )}
              {social.instagram && (
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}><InstagramIcon /></ListItemIcon>
                  <ListItemText primary="Instagram" secondary={<a href={social.instagram} target="_blank" rel="noopener noreferrer">{social.instagram}</a>} />
                </ListItem>
              )}
              {social.linkedin && (
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}><LinkedInIcon /></ListItemIcon>
                  <ListItemText primary="LinkedIn" secondary={<a href={social.linkedin} target="_blank" rel="noopener noreferrer">{social.linkedin}</a>} />
                </ListItem>
              )}
              {social.twitter && (
                <ListItem disablePadding sx={{ mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 40 }}><TwitterIcon /></ListItemIcon>
                  <ListItemText primary="Twitter / X" secondary={<a href={social.twitter} target="_blank" rel="noopener noreferrer">{social.twitter}</a>} />
                </ListItem>
              )}
            </List>
          </Grid>
        </Grid>
      )}

      {/* Tab 1: Зустрічі */}
      {tab === 1 && (
        <Box data-i-doc={TabIndices.CLIENT_EXPERT_APPOINTMENTS}>
          <PageHint iDoc={TabIndices.CLIENT_EXPERT_APPOINTMENTS} />
          <ClientAppointmentsList expertId={expertId} />
        </Box>
      )}

      {/* Tab 2: Доступи */}
      {tab === 2 && isActive && (
        <Box data-i-doc={TabIndices.CLIENT_EXPERT_ACCESS}>
          <PageHint iDoc={TabIndices.CLIENT_EXPERT_ACCESS} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Надані доступи</Typography>
            <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setAccessOpen(true)}>
              Редагувати
            </Button>
          </Box>
          {access && access.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {access.map(permission => (
                <Chip key={permission} label={permission} size="small" icon={<VpnKeyIcon />} />
              ))}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, color: 'text.secondary' }}>
              <VpnKeyIcon sx={{ fontSize: 56, mb: 2, opacity: 0.3 }} />
              <Typography variant="body1">Доступи не надано</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Tab: Відгуки */}
      {tab === (isActive ? 3 : 2) && (
        <Box data-i-doc={TabIndices.EXPERT_PROFILE_REVIEWS}>
          <PageHint iDoc={TabIndices.EXPERT_PROFILE_REVIEWS} />
          {reviewsLoading ? (
            <Box display="flex" justifyContent="center" sx={{ py: 4 }}><CircularProgress /></Box>
          ) : reviews.length === 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, color: 'text.secondary' }}>
              <StarIcon sx={{ fontSize: 56, mb: 2, opacity: 0.3 }} />
              <Typography variant="body1">Відгуків поки немає</Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {reviews.map(review => (
                <Box key={review.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <UserAvatar path={review.client.avatar ?? null} name={review.client.name} sx={{ width: 32, height: 32 }} />
                    <Typography variant="body2" fontWeight={500}>{review.client.name}</Typography>
                    <Rating value={review.rating} readOnly size="small" sx={{ ml: 'auto' }} />
                  </Box>
                  {review.comment && (
                    <Typography variant="body2" color="text.secondary">{review.comment}</Typography>
                  )}
                  <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
                    {new Date(review.createdAt).toLocaleDateString('uk-UA')}
                  </Typography>
                </Box>
              ))}
              {reviewsTotal > REVIEWS_LIMIT && (
                <Box display="flex" justifyContent="center" sx={{ mt: 1 }}>
                  <Pagination
                    count={Math.ceil(reviewsTotal / REVIEWS_LIMIT)}
                    page={reviewsPage}
                    onChange={(_, p) => { setReviewsPage(p); loadReviews(p); }}
                  />
                </Box>
              )}
            </Box>
          )}
        </Box>
      )}

      {consultationOpen && (
        <ConsultationRequestModal
          expert={expertAsListItem}
          open={consultationOpen}
          onClose={(isSuccess) => {
            setConsultationOpen(false);
            if (isSuccess) {
              toastService.success('Запис успішно створено!');
              reload();
            }
          }}
        />
      )}

      {accessOpen && (
        <ConnectionPermissionsDialog
          open={accessOpen}
          onClose={() => setAccessOpen(false)}
          expertId={expertId}
          expertName={general.name}
          mode="edit"
          onSaved={() => {
            toastService.success('Доступи оновлено!');
            reload();
          }}
        />
      )}
    </Container>
  );
};

export default ExpertPage;
